import React, { useState, useEffect } from "react";
import { Col, Row, Card, Button, Table, Spinner, Form } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";

const API_BASE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? window.location.origin : 'https://sgrloto.com';

export default () => {
  const [mode, setMode] = useState("general"); // general, agent, succursal
  const [filtersData, setFiltersData] = useState({ branches: [], agents: [], draws: [] });
  const [loadingFilters, setLoadingFilters] = useState(true);
  
  // Selected filter states
  const [selectedDraw, setSelectedDraw] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedGameType, setSelectedGameType] = useState("Tout");

  const GAME_TYPES = [
    { value: 'Tout', label: 'Tout' },
    { value: 'BORLETTE', label: 'Borlette' },
    { value: 'MARIAGE', label: 'Mariage' },
    { value: 'LOTO3', label: 'Loto3' },
    { value: 'LOTO4', label: 'Loto4' },
    { value: 'LOTO5', label: 'Loto5' },
  ];

  // Confirmed filter states (when Valider is clicked)
  const [validatedParams, setValidatedParams] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Load dropdown lists (agents, branches, draws)
  const fetchFilters = async () => {
    const token = localStorage.getItem("access_token");
    const headers = token ? { "Authorization": `Bearer ${token}` } : {};
    try {
      const res = await fetch(`${API_BASE}/api/reports/filters/`, { headers });
      if (res.ok) {
        const data = await res.json();
        setFiltersData(data);
        // Default select first draw if available
        if (data.draws && data.draws.length > 0) {
          setSelectedDraw(data.draws[0].slug);
        }
      }
    } catch (err) {
      console.error("Failed to fetch filters data:", err);
    }
    setLoadingFilters(false);
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  const handleValidate = async () => {
    setLoadingData(true);
    const token = localStorage.getItem("access_token");
    const headers = token ? { "Authorization": `Bearer ${token}` } : {};

    // Use the draw slug directly (selectedDraw now stores the real slug from DB)
    let drawSlug = "";
    if (selectedDraw && selectedDraw !== "Tout") {
      drawSlug = selectedDraw;
    } else {
      // Default to first active draw or empty
      if (filtersData.draws && filtersData.draws.length > 0) {
        drawSlug = filtersData.draws[0].slug;
      }
    }

    // Find the draw name for display purposes
    const drawName = filtersData.draws.find(d => d.slug === drawSlug)?.name || drawSlug;

    setValidatedParams({
      draw: drawName || "Tout",
      date: selectedDate,
      agent: selectedAgent,
      branch: selectedBranch
    });

    let url = `${API_BASE}/api/risk/surveillance/?draw=${drawSlug || 'florida-matin'}&date=${selectedDate}`;
    if (selectedGameType && selectedGameType !== "Tout") {
      url += `&game_types=${selectedGameType}`;
    }
    if (mode === "agent" && selectedAgent && selectedAgent !== "Tout") {
      url += `&agent=${selectedAgent}`;
    }
    if (mode === "succursal" && selectedBranch && selectedBranch !== "Tout") {
      url += `&branch=${selectedBranch}`;
    }

    try {
      const res = await fetch(url, { headers });
      if (res.ok) {
        const data = await res.json();
        setTableData(data.combinations || []);
      } else {
        setTableData([]);
      }
    } catch (err) {
      console.error("Failed to fetch statistics data:", err);
      setTableData([]);
    }
    setLoadingData(false);
  };

  const getFilteredTableData = () => {
    if (!searchQuery.trim()) return tableData;
    return tableData.filter(row => 
      row.game_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.number.includes(searchQuery)
    );
  };

  const filteredData = getFilteredTableData();

  return (
    <div style={{ padding: '16px 0' }}>
      <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '8px' }}>
        <Card.Header className="bg-white border-0 pt-3 pb-0">
          <h4 style={{ fontWeight: 'bold', color: '#333', margin: 0 }}>Statistique</h4>
        </Card.Header>

        <Card.Body className="pt-2">
          {/* Tabs matching general, par agent, par succursal */}
          <div className="d-flex mb-4" style={{ gap: '10px' }}>
            <Button
              style={{
                backgroundColor: mode === "general" ? '#007BFF' : 'transparent',
                borderColor: '#007BFF',
                color: mode === "general" ? '#FFF' : '#007BFF',
                borderRadius: '4px',
                fontSize: '0.85rem',
                padding: '6px 16px',
                fontWeight: '500'
              }}
              onClick={() => { setMode("general"); setTableData([]); setValidatedParams(null); }}
            >
              General
            </Button>
            <Button
              style={{
                backgroundColor: mode === "agent" ? '#28A745' : 'transparent',
                borderColor: '#28A745',
                color: mode === "agent" ? '#FFF' : '#28A745',
                borderRadius: '4px',
                fontSize: '0.85rem',
                padding: '6px 16px',
                fontWeight: '500'
              }}
              onClick={() => { setMode("agent"); setTableData([]); setValidatedParams(null); }}
            >
              Par agent
            </Button>
            <Button
              style={{
                backgroundColor: mode === "succursal" ? '#FFC107' : 'transparent',
                borderColor: '#FFC107',
                color: mode === "succursal" ? '#222' : '#FFC107',
                borderRadius: '4px',
                fontSize: '0.85rem',
                padding: '6px 16px',
                fontWeight: '500'
              }}
              onClick={() => { setMode("succursal"); setTableData([]); setValidatedParams(null); }}
            >
              Par succursal
            </Button>
          </div>

          {loadingFilters ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" variant="primary" />
              <span className="ms-2 text-muted small">Chargement des filtres...</span>
            </div>
          ) : (
            /* Adaptive filter forms based on mode selection */
            <Row className="mb-3 align-items-end">
              {mode === "general" && (
                <>
                  <Col xs={12} md={4} className="mb-3 mb-md-0">
                    <Form.Label className="small fw-bold mb-1" style={{ color: '#111' }}>Tirage</Form.Label>
                    <div className="position-relative d-flex align-items-center">
                      <Form.Select 
                        style={{ borderColor: '#28A745', borderRadius: '4px', paddingRight: '36px' }}
                        value={selectedDraw}
                        onChange={(e) => setSelectedDraw(e.target.value)}
                      >
                        <option value="Tout">Tout</option>
                        {filtersData.draws.map(d => (
                          <option key={d.id} value={d.slug}>{d.name}</option>
                        ))}
                      </Form.Select>
                      <FontAwesomeIcon icon={faCheck} className="position-absolute text-success" style={{ right: '28px' }} />
                    </div>
                  </Col>

                  <Col xs={12} md={4} className="mb-3 mb-md-0">
                    <Form.Label className="small fw-bold mb-1" style={{ color: '#111' }}>Date</Form.Label>
                    <div className="position-relative d-flex align-items-center">
                      <Form.Control 
                        type="date"
                        style={{ borderColor: '#28A745', borderRadius: '4px', paddingRight: '36px' }}
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                      />
                      <FontAwesomeIcon icon={faCheck} className="position-absolute text-success" style={{ right: '12px' }} />
                    </div>
                  </Col>

                  <Col xs={12} md={4}>
                    <Form.Label className="small fw-bold mb-1" style={{ color: '#111' }}>Type de Jeu</Form.Label>
                    <div className="position-relative d-flex align-items-center">
                      <Form.Select 
                        style={{ borderColor: '#28A745', borderRadius: '4px', paddingRight: '36px' }}
                        value={selectedGameType}
                        onChange={(e) => setSelectedGameType(e.target.value)}
                      >
                        {GAME_TYPES.map(g => (
                          <option key={g.value} value={g.value}>{g.label}</option>
                        ))}
                      </Form.Select>
                      <FontAwesomeIcon icon={faCheck} className="position-absolute text-success" style={{ right: '28px' }} />
                    </div>
                  </Col>
                </>
              )}

              {mode === "agent" && (
                <>
                  <Col xs={12} md={3} className="mb-3 mb-md-0">
                    <Form.Label className="small fw-bold mb-1" style={{ color: '#111' }}>Date</Form.Label>
                    <div className="position-relative d-flex align-items-center">
                      <Form.Control 
                        type="date"
                        style={{ borderColor: '#28A745', borderRadius: '4px', paddingRight: '36px' }}
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                      />
                      <FontAwesomeIcon icon={faCheck} className="position-absolute text-success" style={{ right: '12px' }} />
                    </div>
                  </Col>

                  <Col xs={12} md={3} className="mb-3 mb-md-0">
                    <Form.Label className="small fw-bold mb-1" style={{ color: '#111' }}>Tirage</Form.Label>
                    <div className="position-relative d-flex align-items-center">
                      <Form.Select 
                        style={{ borderColor: '#28A745', borderRadius: '4px', paddingRight: '36px' }}
                        value={selectedDraw}
                        onChange={(e) => setSelectedDraw(e.target.value)}
                      >
                        <option value="Tout">Tout</option>
                        {filtersData.draws.map(d => (
                          <option key={d.id} value={d.slug}>{d.name}</option>
                        ))}
                      </Form.Select>
                      <FontAwesomeIcon icon={faCheck} className="position-absolute text-success" style={{ right: '28px' }} />
                    </div>
                  </Col>

                  <Col xs={12} md={3} className="mb-3 mb-md-0">
                    <Form.Label className="small fw-bold mb-1" style={{ color: '#111' }}>Type de Jeu</Form.Label>
                    <div className="position-relative d-flex align-items-center">
                      <Form.Select 
                        style={{ borderColor: '#28A745', borderRadius: '4px', paddingRight: '36px' }}
                        value={selectedGameType}
                        onChange={(e) => setSelectedGameType(e.target.value)}
                      >
                        {GAME_TYPES.map(g => (
                          <option key={g.value} value={g.value}>{g.label}</option>
                        ))}
                      </Form.Select>
                      <FontAwesomeIcon icon={faCheck} className="position-absolute text-success" style={{ right: '28px' }} />
                    </div>
                  </Col>

                  <Col xs={12} md={3}>
                    <Form.Label className="small fw-bold mb-1" style={{ color: '#111' }}>Agent</Form.Label>
                    <div className="position-relative d-flex align-items-center">
                      <Form.Select 
                        style={{ borderColor: '#28A745', borderRadius: '4px', paddingRight: '36px' }}
                        value={selectedAgent}
                        onChange={(e) => setSelectedAgent(e.target.value)}
                      >
                        <option value="Tout">Tout</option>
                        {filtersData.agents.map(a => (
                          <option key={a.id} value={a.id}>
                            {a.username} ({a.first_name} {a.last_name})
                          </option>
                        ))}
                      </Form.Select>
                      <FontAwesomeIcon icon={faCheck} className="position-absolute text-success" style={{ right: '28px' }} />
                    </div>
                  </Col>
                </>
              )}

              {mode === "succursal" && (
                <>
                  <Col xs={12} md={3} className="mb-3 mb-md-0">
                    <Form.Label className="small fw-bold mb-1" style={{ color: '#111' }}>Date</Form.Label>
                    <div className="position-relative d-flex align-items-center">
                      <Form.Control 
                        type="date"
                        style={{ borderColor: '#28A745', borderRadius: '4px', paddingRight: '36px' }}
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                      />
                      <FontAwesomeIcon icon={faCheck} className="position-absolute text-success" style={{ right: '12px' }} />
                    </div>
                  </Col>

                  <Col xs={12} md={3} className="mb-3 mb-md-0">
                    <Form.Label className="small fw-bold mb-1" style={{ color: '#111' }}>Tirage</Form.Label>
                    <div className="position-relative d-flex align-items-center">
                      <Form.Select 
                        style={{ borderColor: '#28A745', borderRadius: '4px', paddingRight: '36px' }}
                        value={selectedDraw}
                        onChange={(e) => setSelectedDraw(e.target.value)}
                      >
                        <option value="Tout">Tout</option>
                        {filtersData.draws.map(d => (
                          <option key={d.id} value={d.slug}>{d.name}</option>
                        ))}
                      </Form.Select>
                      <FontAwesomeIcon icon={faCheck} className="position-absolute text-success" style={{ right: '28px' }} />
                    </div>
                  </Col>

                  <Col xs={12} md={3} className="mb-3 mb-md-0">
                    <Form.Label className="small fw-bold mb-1" style={{ color: '#111' }}>Type de Jeu</Form.Label>
                    <div className="position-relative d-flex align-items-center">
                      <Form.Select 
                        style={{ borderColor: '#28A745', borderRadius: '4px', paddingRight: '36px' }}
                        value={selectedGameType}
                        onChange={(e) => setSelectedGameType(e.target.value)}
                      >
                        {GAME_TYPES.map(g => (
                          <option key={g.value} value={g.value}>{g.label}</option>
                        ))}
                      </Form.Select>
                      <FontAwesomeIcon icon={faCheck} className="position-absolute text-success" style={{ right: '28px' }} />
                    </div>
                  </Col>

                  <Col xs={12} md={3}>
                    <Form.Label className="small fw-bold mb-1" style={{ color: '#111' }}>Succursal</Form.Label>
                    <div className="position-relative d-flex align-items-center">
                      <Form.Select 
                        style={{ borderColor: '#28A745', borderRadius: '4px', paddingRight: '36px' }}
                        value={selectedBranch}
                        onChange={(e) => setSelectedBranch(e.target.value)}
                      >
                        <option value="Tout">Tout</option>
                        {filtersData.branches.map(b => (
                          <option key={b.id} value={b.id}>{b.name}</option>
                        ))}
                      </Form.Select>
                      <FontAwesomeIcon icon={faCheck} className="position-absolute text-success" style={{ right: '28px' }} />
                    </div>
                  </Col>
                </>
              )}
            </Row>
          )}

          {/* Valider Button */}
          <Button
            style={{
              backgroundColor: '#007BFF',
              borderColor: '#007BFF',
              color: '#FFF',
              fontWeight: '600',
              padding: '10px 0',
              borderRadius: '4px',
              width: '100%'
            }}
            onClick={handleValidate}
          >
            Valider
          </Button>
        </Card.Body>
      </Card>

      {/* Statistics results block */}
      {validatedParams && (
        <Card className="border-0 shadow-sm" style={{ borderRadius: '8px' }}>
          <Card.Header className="bg-white border-0 py-3">
            <h5 className="mb-0" style={{ fontWeight: 'bold', color: '#111' }}>
              Tirage : {validatedParams.draw.toUpperCase()}
            </h5>
          </Card.Header>

          {/* Export buttons & search */}
          <div className="px-3 pb-3 d-flex justify-content-between align-items-center flex-wrap" style={{ gap: '10px' }}>
            <div className="d-flex" style={{ gap: '4px' }}>
              {["COPIER", "EXCEL", "PDF", "IMPRIMER"].map((label, idx) => (
                <button
                  key={idx}
                  style={{
                    backgroundColor: '#F5F5F5',
                    border: '1px solid #CCCCCC',
                    color: '#333333',
                    fontSize: '0.78rem',
                    padding: '6px 16px',
                    borderRadius: '2px',
                    height: '34px',
                    fontWeight: 'normal',
                    outline: 'none'
                  }}
                  onClick={() => alert(`${label} cliqué`)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="d-flex align-items-center" style={{ gap: '8px' }}>
              <span className="fw-bold small text-dark" style={{ fontSize: '0.92rem' }}>Search:</span>
              <Form.Control 
                type="text" 
                size="sm" 
                style={{ width: '220px', borderRadius: '4px', border: '1px solid #CCC' }} 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Table list */}
          <div className="table-responsive px-3 pb-3">
            {loadingData ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <div className="text-muted small mt-2">Chargement des données statistiques...</div>
              </div>
            ) : (
              <Table bordered hover className="align-items-center mb-0" style={{ borderCollapse: 'collapse', borderColor: '#EAEAEA' }}>
                <thead>
                  <tr style={{ backgroundColor: '#FAF9F9' }}>
                    <th style={{ padding: '12px 10px', fontSize: '0.85rem', color: '#333', fontWeight: 'bold' }}>Type</th>
                    <th style={{ padding: '12px 10px', fontSize: '0.85rem', color: '#333', fontWeight: 'bold' }}>Boule</th>
                    <th style={{ padding: '12px 10px', fontSize: '0.85rem', color: '#333', fontWeight: 'bold' }}>Quantité fois</th>
                    <th style={{ padding: '12px 10px', fontSize: '0.85rem', color: '#333', fontWeight: 'bold' }}>Montant Total</th>
                    <th style={{ padding: '12px 10px', fontSize: '0.85rem', borderLeft: 'none' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length > 0 ? (
                    filteredData.map((row, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: '10px', fontSize: '0.85rem', color: '#333' }}>{row.game_type}</td>
                        <td style={{ padding: '10px', fontSize: '0.85rem', color: '#333', fontWeight: 'bold' }}>{row.number}</td>
                        <td style={{ padding: '10px', fontSize: '0.85rem', color: '#333' }}>{row.quantity}</td>
                        <td style={{ padding: '10px', fontSize: '0.85rem', color: '#333' }}>{Math.round(parseFloat(row.total_amount) || 0)} / {row.limit || 5000} HTG</td>
                        <td style={{ padding: '10px', borderLeft: 'none' }}></td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-4 text-muted small" style={{ fontWeight: '500' }}>
                        No data available in table
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </div>

          {/* Footer pagination */}
          <div className="p-3 d-flex justify-content-between align-items-center border-top">
            <span className="small text-muted fw-bold">
              Showing {filteredData.length > 0 ? 1 : 0} to {filteredData.length} of {filteredData.length} entries
            </span>
            <div className="d-flex" style={{ gap: '2px' }}>
              <Button variant="light" className="border btn-sm text-dark font-weight-bold" style={{ backgroundColor: '#F8F9FA', borderRadius: '2px' }}>Previous</Button>
              <Button variant="light" className="border btn-sm text-dark font-weight-bold" style={{ backgroundColor: '#F8F9FA', borderRadius: '2px' }}>Next</Button>
            </div>
          </div>
        </Card>
      )}

      {/* Main Bottom Imprimer Button */}
      <div className="text-center mt-4">
        <Button
          style={{
            backgroundColor: '#007BFF',
            borderColor: '#007BFF',
            color: '#FFF',
            padding: '8px 24px',
            fontSize: '0.9rem',
            borderRadius: '4px',
            fontWeight: '600'
          }}
          onClick={() => alert("Imprimer la page complète")}
        >
          Imprimer
        </Button>
      </div>
    </div>
  );
};
